/**
 * UnresolvedTextNotice.jsx
 *
 * Says, BEFORE ten hours of audio get made, that the book still contains text somebody could
 * correct in a minute.
 *
 * WHY IT EXISTS HERE rather than as another editor. The corrections themselves live in the Ingest
 * Lab and belong there — applying one means a re-ingest, which rebuilds the script and supersedes
 * the renders of every line whose text changed. Duplicating that UI in the Studio would buy one
 * saved click in exchange for a second copy to keep in step. What the Studio genuinely owes the
 * operator is the WARNING, at the only moment it is cheap to act on: Kindle read "St Coriander"
 * as "Street Coriander" through an entire series, and the way you find that out is half way
 * through listening to the finished book.
 *
 * It also owes an ALL-CLEAR. See the three states below — a check that only speaks up when it has
 * bad news is indistinguishable from a check that never ran.
 *
 * THE NUMBERS ARE THE POINT. "22 items" is a chore. "297 places in this book" is a reason, and it
 * is also roughly what a later fix would cost in re-rendered lines — so the notice states the
 * price of acting now and, by implication, of not.
 */

import React from 'react';
import { Message, Icon, Button } from 'semantic-ui-react';

const plural = (n, one, many) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

const UnresolvedTextNotice = ({ review, onOpenIngestLab }) => {
  /**
   * THREE STATES, AND ALL THREE SAY WHICH ONE THEY ARE.
   *
   * The first version stayed silent when nothing was outstanding, on the reasoning that the
   * Studio is for rendering and is not where you go to check. That was wrong for the same reason
   * it was wrong in the Ingest Lab: silence cannot distinguish "this book is clean" from "this
   * check never ran", and the difference matters most right before a long render.
   *
   * The third state is the one that made it clearly wrong. A book last ingested before these
   * counters existed carries none at all — and an absent count rendered exactly like a clean one.
   */
  if (!review) {
    return (
      <Message icon size="small" style={{ marginBottom: '1em' }}>
        <Icon name="question circle outline" />
        <Message.Content>
          <Message.Header>Text not checked yet</Message.Header>
          This book was last ingested before the text check existed, so there is nothing to report
          either way. A re-ingest will tell you whether anything needs fixing.
        </Message.Content>
      </Message>
    );
  }

  const { textItems = 0, textOccurrences = 0, hyphens = 0 } = review;

  if (!textItems && !hyphens) {
    return (
      <Message positive icon size="small" style={{ marginBottom: '1em' }}>
        <Icon name="check circle" />
        <Message.Content>
          {/* ⚠️ "Review complete", NOT "text validated". The claim is scoped to the REVIEW, and it
              has to be: this reports only on items the INGEST FLAGGED, which is a small fraction of
              the book. LN1 showed this banner green while its appendix still carried 11 hyphenated
              words the narrator reads broken and a misspelled name in a footnote — none flagged,
              so none outstanding. A header saying the text is valid would have been a guarantee
              nothing here can make, read by someone with no way to know the difference. */}
          <Message.Header>Text review complete</Message.Header>
          Every correction and hyphen this book offered has been decided. Nothing the ingest flagged
          is still outstanding.
        </Message.Content>
      </Message>
    );
  }

  const parts = [];
  if (textItems) {
    parts.push(
      textOccurrences > textItems
        ? `${plural(textItems, 'text correction', 'text corrections')} covering ${plural(textOccurrences, 'place', 'places')} in the book`
        : plural(textItems, 'text correction', 'text corrections')
    );
  }
  if (hyphens) parts.push(plural(hyphens, 'unresolved hyphen', 'unresolved hyphens'));

  return (
    <Message warning icon style={{ marginBottom: '1em' }}>
      <Icon name="edit outline" />
      <Message.Content>
        <Message.Header>Fix the text before you render</Message.Header>
        <p style={{ marginBottom: '0.5em' }}>
          This book still has {parts.join(' and ')}. They are cheap to fix now and expensive later —
          correcting text after a render supersedes every line that changed, and those lines have to
          be rendered again.
        </p>
        <p style={{ marginBottom: '0.6em', color: '#7a5b1e' }}>
          Typical culprits: an abbreviation a synthetic reader guesses at (&ldquo;St Coriander&rdquo;
          read as <em>Street</em> Coriander), or a word the book&apos;s letter spacing pulled apart.
        </p>
        {onOpenIngestLab && (
          <Button size="small" onClick={onOpenIngestLab}>
            <Icon name="i cursor" /> Open the Ingest Lab
          </Button>
        )}
      </Message.Content>
    </Message>
  );
};

export default UnresolvedTextNotice;
